import type { RightCatalogueEntry } from '../api/types';

/**
 * Frontend mirror of `GrantDelegation.ApplyCeiling` on the server: returns a
 * `canDelegate(token)` predicate used to grey out rights a bounded admin can't
 * grant. The server is authoritative — this is a UX hint only.
 *
 * Semantics match `RightExpander.Expand`:
 * - `*`                   → every catalogue key
 * - `feature.*`           → every catalogue key in that feature
 * - `feature.cat.*`       → every catalogue key in that feature for that category
 * - `feature.cat.action`  → just that key (when active)
 *
 * `unbounded` collapses the predicate to "always true" for SP / AdminService.
 */
export function makeDelegationGuard(
  catalogue: RightCatalogueEntry[],
  callerRights: string[] | undefined,
  unbounded: boolean,
): (token: string) => boolean {
  if (unbounded) return () => true;

  const held = new Set((callerRights ?? []).map((k) => k.toLowerCase()));

  // Pre-index the catalogue once for cheap wildcard expansion.
  const byFeature = new Map<string, Set<string>>();
  const byFeatureCategory = new Map<string, Set<string>>();
  const allKeys = new Set<string>();
  for (const r of catalogue ?? []) {
    const fk = r.featureKey.toLowerCase();
    const cat = r.category.toLowerCase();
    const key = r.key.toLowerCase();
    allKeys.add(key);
    if (!byFeature.has(fk)) byFeature.set(fk, new Set());
    byFeature.get(fk)!.add(key);
    const fc = `${fk}.${cat}`;
    if (!byFeatureCategory.has(fc)) byFeatureCategory.set(fc, new Set());
    byFeatureCategory.get(fc)!.add(key);
  }

  const expand = (raw: string): Set<string> => {
    const token = raw.trim().toLowerCase();
    if (token === '*') return allKeys;
    if (token.endsWith('.*')) {
      const prefix = token.slice(0, -2);
      // `feature.category` wins over `feature` — feature names never contain dots.
      return byFeatureCategory.get(prefix) ?? byFeature.get(prefix) ?? new Set();
    }
    return allKeys.has(token) ? new Set([token]) : new Set();
  };

  return (raw: string) => {
    const expanded = expand(raw);
    if (expanded.size === 0) return false;
    for (const k of expanded) if (!held.has(k)) return false;
    return true;
  };
}
